import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faBuilding,
  faMoneyBillTrendUp,
  faShieldHalved,
  faUser,
  faUserLock,
} from "@fortawesome/pro-regular-svg-icons";
import { SortingState } from "@tanstack/react-table";

import {
  DropdownSelection,
  LATEST_VERSION_ID,
} from "src/cases/common/CaseTemplateDropdown";
import { CASES_DEFAULT_SORT } from "src/cases/queries/queues";
import {
  CaseQueueColumnKey,
  CaseQueueColumnSchema,
  CaseQueueColumnSchemaOption,
  CaseQueueColumnSchemas,
  DeserializedCaseQueue,
  DeserializedCaseTemplateVersion,
  DeserializedQueueCase,
  EnrichedDeserializedQueueCase,
} from "src/cases/queues/types";
import { CasePropertiesValue, EnumValue } from "src/clients/case-management";
import {
  JsonApiResourceObjectUnionQueueMemberAttributesQueueTemplateConfigAttributesQueueDefaultTemplateVersionAttributesAny,
  QueueAtomicOperationsRequest,
  QueueCreateAttributes,
  QueueDefaultTemplateVersionUpdateOperation,
} from "src/clients/case-management-service";

export const CASE_QUEUE_ICONS: Record<string, IconProp> = {
  "shield-halved": faShieldHalved,
  "user-lock": faUserLock,
  buildings: faBuilding,
  user: faUser,
  "money-bill-trend-up": faMoneyBillTrendUp,
} as const;

export const getCaseQueueIcon = (icon?: string | null) => {
  if (!icon || !(icon in CASE_QUEUE_ICONS)) return null;
  return CASE_QUEUE_ICONS[icon];
};

const extractDefaultTemplateVersion = (
  queue: DeserializedCaseQueue,
): DropdownSelection | null => {
  const dtv = queue.default_template_version;
  if (!dtv?.template) return null;

  if (dtv.kind === "latest") {
    return { templateId: dtv.template.id, versionId: LATEST_VERSION_ID };
  }

  if (!dtv.template_version?.id) return null;

  return {
    templateId: dtv.template.id,
    versionId: dtv.template_version.id,
  };
};

export const extractInitialFormValues = (queue?: DeserializedCaseQueue) => {
  if (!queue) return;

  return {
    attributes: {
      name: queue.name,
      icon: queue.icon,
    },
    memberIds: queue.members.map(({ user }) => user.id),
    templateIds: queue.template_configs.map(({ template }) => template.id),
    defaultTemplateVersion: extractDefaultTemplateVersion(queue),
  };
};

type CreateQueueAtomicOperationParams = {
  queueId: string;
  attributes: QueueCreateAttributes;
  templateIds: string[];
  memberIds: string[];
  defaultTemplateVersion?: DropdownSelection | null;
};

type QueueAtomicOperationInclude =
  JsonApiResourceObjectUnionQueueMemberAttributesQueueTemplateConfigAttributesQueueDefaultTemplateVersionAttributesAny;

export const DEFAULT_TEMPLATE_VERSION_RESOURCE_ID = "default-template-version";

export const buildDefaultTemplateVersionInclude = (
  selection: DropdownSelection,
): QueueAtomicOperationInclude => {
  const isLatest = selection.versionId === LATEST_VERSION_ID;
  return {
    type: "default_template_versions",
    id: DEFAULT_TEMPLATE_VERSION_RESOURCE_ID,
    attributes: { kind: isLatest ? "latest" : "pinned" },
    relationships: {
      template: { data: { type: "templates", id: selection.templateId } },
      ...(isLatest
        ? {}
        : {
            template_version: {
              data: {
                type: "template_versions",
                id: selection.versionId,
              },
            },
          }),
    },
  } as unknown as QueueAtomicOperationInclude;
};

export const createQueueAtomicOperationIncludes = ({
  memberIds,
  templateIds,
  defaultTemplateVersion,
}: Omit<
  CreateQueueAtomicOperationParams,
  "queueId" | "attributes"
>): QueueAtomicOperationsRequest["included"] => {
  return [
    ...memberIds.map(
      (id) =>
        ({
          type: "queue_members" as const,
          id,
          attributes: { type: "user" },
          relationships: {
            user: { data: { type: "users", id } },
          },
        }) as unknown as QueueAtomicOperationInclude,
    ),
    ...templateIds.map(
      (id) =>
        ({
          type: "template_configs" as const,
          id,
          attributes: { assignment_logic: "self-assign" },
          relationships: {
            template: {
              data: { type: "templates", id },
            },
          },
        }) as unknown as QueueAtomicOperationInclude,
    ),
    ...(defaultTemplateVersion
      ? [buildDefaultTemplateVersionInclude(defaultTemplateVersion)]
      : []),
  ];
};

export const createQueueAtomicOperation = ({
  queueId,
  memberIds,
  attributes,
  templateIds,
  defaultTemplateVersion,
}: CreateQueueAtomicOperationParams): QueueAtomicOperationsRequest["atomic:operations"] => {
  return [
    {
      op: "update",
      ref: {
        type: "queues",
        id: queueId,
        relationship: "attributes",
      },
      data: attributes,
    },
    {
      op: "update",
      ref: {
        type: "queues",
        id: queueId,
        relationship: "template_configs",
      },
      data: templateIds.map((id) => ({
        type: "template_configs",
        id,
      })),
    },
    {
      op: "update",
      ref: {
        type: "queues",
        id: queueId,
        relationship: "members",
      },
      data: memberIds.map((id) => ({
        type: "queue_members",
        id,
      })),
    },
    ...(defaultTemplateVersion !== undefined
      ? [
          {
            op: "update",
            ref: {
              type: "queues",
              id: queueId,
              relationship: "default_template_version",
            },
            data: defaultTemplateVersion
              ? {
                  type: "default_template_versions",
                  id: DEFAULT_TEMPLATE_VERSION_RESOURCE_ID,
                }
              : null,
          } as QueueDefaultTemplateVersionUpdateOperation,
        ]
      : []),
  ];
};

export const RESERVED_PROPERTY_KEYS = ["status", "sub_status"];

export const CASE_STATUS_COLUMN_ID = "status";

/**
 * Reorderable built-in columns, in the position they hold by default relative
 * to the case property columns the queue's merged templates contribute.
 */
const BUILT_IN_COLUMN_IDS_BEFORE_PROPERTIES = [
  "template_id",
  "template_status",
];
const BUILT_IN_COLUMN_IDS_AFTER_PROPERTIES = [
  CASE_STATUS_COLUMN_ID,
  "assignee",
  "created_at",
  "updated_at",
];

export const getCasePropertyColumnIds = (
  columnSchemas: CaseQueueColumnSchemas,
): string[] =>
  Object.entries(columnSchemas)
    .filter(
      ([, columnSchema]) =>
        columnSchema && !RESERVED_PROPERTY_KEYS.includes(columnSchema.__key),
    )
    .map(([columnId]) => columnId);

/**
 * Default left-to-right order of the reorderable case columns. The selector,
 * Case ID and actions columns are pinned and stay out of it.
 */
export const getDefaultCaseColumnOrder = (
  columnSchemas: CaseQueueColumnSchemas,
): string[] => [
  ...BUILT_IN_COLUMN_IDS_BEFORE_PROPERTIES,
  ...getCasePropertyColumnIds(columnSchemas),
  ...BUILT_IN_COLUMN_IDS_AFTER_PROPERTIES,
];

/**
 * A queue merges several case templates, so a template added later brings
 * columns a stored order has never seen. Those land at the end of the order
 * but ahead of the status column, which stays the right-most case column.
 */
export const reconcileCaseColumnOrder = (
  defaultOrder: string[],
  storedOrder: string[],
): string[] => {
  const defaultIds = new Set(defaultOrder);
  const storedIds = new Set(storedOrder);
  const keptIds = storedOrder.filter((id) => defaultIds.has(id));
  const newIds = defaultOrder.filter((id) => !storedIds.has(id));
  const statusIndex = keptIds.indexOf(CASE_STATUS_COLUMN_ID);
  const insertAt = statusIndex === -1 ? keptIds.length : statusIndex;

  return [...keptIds.slice(0, insertAt), ...newIds, ...keptIds.slice(insertAt)];
};

const SORTABLE_PROPERTY_TYPES = ["number", "integer", "datetime"] as const;

const BUILT_IN_SORT_COLUMN_IDS: readonly string[] = [
  "created_at",
  "updated_at",
];

const isSortablePropertyType = (type: string): boolean =>
  SORTABLE_PROPERTY_TYPES.includes(
    type as (typeof SORTABLE_PROPERTY_TYPES)[number],
  );

export const isColumnSortable = (
  columnSchema: CaseQueueColumnSchema,
): boolean => isSortablePropertyType(columnSchema.type);

/**
 * The v2 cases endpoint accepts only the built-in sorts and
 * `properties.<key>+<kind>`, and answers anything else with a 422. A column id
 * that maps to neither cannot be sorted by, so both the request and the table
 * header fall back to the default sort.
 */
export const isSortableColumnId = (columnId: string): boolean => {
  if (!columnId.includes(".")) {
    return BUILT_IN_SORT_COLUMN_IDS.includes(columnId);
  }

  const [propType, propKey] = columnId.split(".");

  return !!propKey && isSortablePropertyType(propType);
};

export const sortingToApiSortParam = (sorting: SortingState): string => {
  if (sorting.length === 0) return CASES_DEFAULT_SORT;

  const { id: columnId, desc } = sorting[0];
  if (!isSortableColumnId(columnId)) return CASES_DEFAULT_SORT;

  const [propType, propKey] = columnId.split(".");
  const sortParam = propKey ? `properties.${propKey}+${propType}` : columnId;

  return desc ? `-${sortParam}` : sortParam;
};

/**
 * Flattens predefined enum values into column schema options, expanding a value
 * with sub-values into one option per status/sub-status pair.
 */
const toColumnSchemaOptions = (
  values: EnumValue[],
): CaseQueueColumnSchemaOption[] =>
  values.flatMap((value) => {
    const option = {
      value: value.value,
      displayName: value.display_name || value.value,
      color: value.color ?? undefined,
    };

    if (!value.sub_values?.length) {
      return [option];
    }

    return value.sub_values.map((subValue) => ({
      ...option,
      subValue: {
        value: subValue.value,
        displayName: subValue.display_name || subValue.value,
      },
    }));
  });

const createOptionKey = (option: CaseQueueColumnSchemaOption): string =>
  JSON.stringify([option.value, option.subValue?.value]);

const hasRealDisplayName = (option: CaseQueueColumnSchemaOption): boolean =>
  option.displayName !== option.value;

/**
 * Deduplicates options by value/sub-value pair, preferring options with a real
 * displayName over ones where displayName is just the raw value fallback.
 */
const dedupeColumnSchemaOptions = (
  options: CaseQueueColumnSchemaOption[],
): CaseQueueColumnSchemaOption[] => {
  const optionsByKey = options.reduce<Map<string, CaseQueueColumnSchemaOption>>(
    (byKey, option) => {
      const existingOption = byKey.get(createOptionKey(option));

      if (
        !existingOption ||
        (!hasRealDisplayName(existingOption) && hasRealDisplayName(option))
      ) {
        byKey.set(createOptionKey(option), option);
      }

      return byKey;
    },
    new Map(),
  );

  return Array.from(optionsByKey.values());
};

/**
 *  Extracts a unique set of column schemas from an array of case template versions.
 *
 * This utility processes multiple case template version objects and aggregates
 * all unique case property schemas from their `case_properties` fields into a flat object.
 * A unique schema key (generated by `createColumnKey`) ensures that each schema
 * is only included once, even if it appears in multiple versions.
 *
 */
export const extractQueueColumnSchemas = (
  versions: DeserializedCaseTemplateVersion[],
) => {
  return versions.reduce((columnSchemas, version) => {
    const propertyEntries = Object.entries(version.case_properties);

    propertyEntries.forEach(([propertyKey, schema]) => {
      const propertySchema = schema as CasePropertiesValue;

      const schemaKey = createColumnKey(propertyKey, propertySchema);
      const columnSchema = columnSchemas[schemaKey];

      const withOptions =
        propertySchema.type === "enum" || propertySchema.type === "tags";

      if (columnSchema) {
        columnSchema.template_version_ids.add(version.id);
        // if the schema is enum or tags, we need to merge options
        if (withOptions && columnSchema.type === propertySchema.type) {
          columnSchema.options = dedupeColumnSchemaOptions([
            ...columnSchema.options,
            ...toColumnSchemaOptions(propertySchema.values ?? []),
          ]);
        }
      } else {
        if (withOptions) {
          // create new schema with options
          columnSchemas[schemaKey] = {
            template_version_ids: new Set([version.id]),
            type: propertySchema.type,
            display_name: propertySchema.display_name,
            options: toColumnSchemaOptions(propertySchema.values ?? []),
            __key: propertyKey,
          };
        } else if (propertySchema.type === "entity_ref") {
          columnSchemas[schemaKey] = {
            template_version_ids: new Set([version.id]),
            type: "entity_ref",
            display_name: propertySchema.display_name,
            __key: propertyKey,
            entity_type: propertySchema.entity_type,
          };
        } else if (propertySchema.type === "event_ref") {
          columnSchemas[schemaKey] = {
            template_version_ids: new Set([version.id]),
            type: "event_ref",
            display_name: propertySchema.display_name,
            __key: propertyKey,
            event_type: propertySchema.event_type,
          };
        } else {
          columnSchemas[schemaKey] = {
            template_version_ids: new Set([version.id]),
            type: propertySchema.type,
            display_name: propertySchema.display_name,
            __key: propertyKey,
          };
        }
      }
    });

    return columnSchemas;
  }, {} as CaseQueueColumnSchemas);
};

export const createColumnKey = (
  key: string,
  schema: CasePropertiesValue,
): CaseQueueColumnKey => {
  let schemaKey = `${schema.type}.${key}`;

  if (!RESERVED_PROPERTY_KEYS.includes(key)) {
    const displayName = schema.display_name.toLowerCase().replaceAll(" ", "_");
    schemaKey += `.${displayName}`;
  }

  return schemaKey as CaseQueueColumnKey;
};

/**
 * Enriches a case object by mapping its custom properties onto
 * standardized column keys based on provided column schemas
 */
export const enrichCaseWithColumnValues = (
  caseData: DeserializedQueueCase,
  queueColumnSchemas: CaseQueueColumnSchemas,
) => {
  const enrichedCaseData = {
    ...caseData,
  } as EnrichedDeserializedQueueCase;

  const customProperties = caseData.case_properties;
  const columnKeys = Object.keys(queueColumnSchemas).filter(
    (key): key is CaseQueueColumnKey => !!key,
  );

  for (const columnKey of columnKeys) {
    const columnSchema = queueColumnSchemas[columnKey];
    enrichedCaseData[columnKey] = columnSchema
      ? customProperties[columnSchema.__key]
      : undefined;
  }

  return enrichedCaseData;
};
